import { DataModel } from 'legacy/lut/classes/models/DataModel'
import businessProfileStoreService from 'legacy/lut/classes/services/BusinessProfileStoreService'
import { useCountryCode } from 'legacy/lut/hooks/useCountryCode'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { IPartner } from 'clickNgo/entities/partner/models'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Button } from 'core-components/library/button'
import { ModalDialog } from 'core-components/library/modalDialog'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'

import { SaveAsDraftContact } from './SaveAsDraftContact'

import styles from './DraftContactDialog.module.scss'

interface IDraftContactDialogProps {
  isVisible: boolean
  partner: IPartner

  appSource?: string
  onClose: () => void
  onCreateAsDraft: () => void
  onContinueContactCreation?: (data: DataModel | null) => void
}

export const DraftContactDialog = ({
  isVisible,
  partner,
  appSource,
  onClose,
  onContinueContactCreation,
  onCreateAsDraft,
}: IDraftContactDialogProps) => {
  const [showWarning, setShowWarning] = useState(false)
  const [contact, setContact] = useState<DataModel | null>(null)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const countryCode = useCountryCode(partner?.country ?? '')
  const user = useSelector(selectUser)

  useEffect(() => {
    if (!isVisible) {
      setShowWarning(false)
      setContact(null)
      setIsSavingDraft(false)
    }
  }, [isVisible])

  const draftModel = useMemo(() => {
    const model = new DataModel({})

    if (partner) {
      model.set('PartnerID', partner.partnerId)
      model.set('Market', partner.market)
      if (partner.countryCode) {
        model.set('CountryOfBusiness', partner.countryCode)
      } else {
        model.set('CountryOfBusiness', countryCode)
      }
    }

    return model
  }, [partner, countryCode])

  const onApply = (contact: DataModel) => {
    if (contact) {
      setContact(contact)
    }
  }

  if (!isVisible) return null

  return (
    <>
      {/* Save as draft */}
      <ModalDialog
        size="xs"
        show
        onClose={onClose}
        content={SaveAsDraftContact}
        contentProps={{
          draftContact: draftModel,
          appSource,
          onApply,
          onClose: (needWarning: boolean) => {
            needWarning ? setShowWarning(true) : onClose?.()
          },
        }}
      />

      {/* Warning */}
      <ModalDialog
        show={showWarning}
        size={'xs'}
        onClose={() => setShowWarning(false)}
        content={() => {
          return (
            <div className={styles.modal}>
              <LoaderOverlay isVisible={isSavingDraft} color="white" />
              <p className={styles.modal__title}>
                Contact is saved as draft and{' '}
                <u>will be created after the partner submits their company details.</u>
                <br />
                <br />
                <b>Important!</b> If you are adding a new contact as <b>L&R Champion</b>, then click
                on ‘Create contact’, fill in all mandatory details, assign L&R Champion role and
                click on ‘Apply’ button to create the contact.
                <br />
              </p>

              <footer className={styles.modal__footer}>
                <Button
                  className={styles.modal__button}
                  disabled={isSavingDraft}
                  onClick={() => {
                    if (isSavingDraft) return
                    setShowWarning(false)
                    onContinueContactCreation?.(contact)
                  }}
                >
                  Create contact
                </Button>

                <Button
                  className={styles.modal__button}
                  variant={'outlined'}
                  color={'danger'}
                  disabled={isSavingDraft}
                  onClick={async () => {
                    if (!contact || isSavingDraft) return

                    setIsSavingDraft(true)

                    try {
                      await businessProfileStoreService.changeProfileExt(
                        contact,
                        'Draft',
                        appSource,
                        user.email
                      )

                      await onCreateAsDraft?.()
                    } finally {
                      setIsSavingDraft(false)
                    }
                  }}
                >
                  Leave contact as draft
                </Button>
              </footer>
            </div>
          )
        }}
      />
    </>
  )
}
